import { useEffect, useState } from "react";
import http from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmt } from "./AdminKit";
import { UserPlus, Trash2 } from "lucide-react";

const EMPTY = { username: "", display_name: "", role: "workshop", password: "", is_active: true };
const roleLabel = { admin: "Администратор", workshop: "Приёмка (огранич.)" };

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const meId = localStorage.getItem("admin_id") || "";

  const load = async () => {
    try { const r = await http.get("/v1/admin/users"); setRows(r.data || []); }
    catch { toast.error("Не удалось загрузить пользователей"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ username: u.username, display_name: u.display_name || "", role: u.role, password: "", is_active: u.is_active !== false }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      if (editId) {
        await http.put(`/v1/admin/users/${editId}`, form);
        toast.success("Пользователь обновлён");
      } else {
        await http.post("/v1/admin/users", form);
        toast.success("Пользователь создан");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ошибка сохранения"); }
    finally { setBusy(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Удалить пользователя ${u.username}?`)) return;
    try { await http.delete(`/v1/admin/users/${u.id}`); toast.success("Удалён"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Ошибка"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Пользователи</h1>
          <p className="text-sm text-slate-400">Администраторы и ограниченные пользователи приёмки (workshop).</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="user-new"><UserPlus className="h-4 w-4 mr-1" />Новый</Button>
      </div>

      <div className="rounded-lg border border-slate-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 text-slate-300"><tr>
            <th className="text-left p-2">Логин</th><th className="text-left p-2">Имя</th><th className="text-left p-2">Роль</th><th className="text-left p-2">Статус</th><th className="text-left p-2">Создан</th><th className="p-2"></th>
          </tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-700 hover:bg-slate-700/30" data-testid={`user-row-${u.username}`}>
                <td className="p-2"><button className="text-blue-300 hover:underline" onClick={() => openEdit(u)}>{u.username}</button></td>
                <td className="p-2 text-slate-300">{u.display_name || "—"}</td>
                <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${u.role === "admin" ? "bg-blue-900/40 text-blue-200" : "bg-amber-900/40 text-amber-200"}`}>{roleLabel[u.role] || u.role}</span></td>
                <td className="p-2 text-slate-300">{u.is_active === false ? "Неактивен" : "Активен"}</td>
                <td className="p-2 text-slate-500 text-xs">{fmt(u.created_at)}</td>
                <td className="p-2 text-right">{u.id !== meId && <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => del(u)} data-testid={`user-del-${u.username}`}><Trash2 className="h-4 w-4" /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="text-white">{editId ? "Изменить пользователя" : "Новый пользователь"}</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">Роль «Приёмка» ограничивает доступ к финансам и редактированию.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-400">Логин</label>
              <Input value={form.username} disabled={!!editId} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="user-username" /></div>
            <div><label className="text-xs text-slate-400">Отображаемое имя</label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="user-displayname" /></div>
            <div><label className="text-xs text-slate-400">Роль</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full rounded-md bg-slate-700 border border-slate-600 text-white p-2 text-sm" data-testid="user-role">
                <option value="workshop">Приёмка (ограниченная)</option>
                <option value="admin">Администратор (полный доступ)</option>
              </select></div>
            <div><label className="text-xs text-slate-400">{editId ? "Новый пароль (оставьте пустым, чтобы не менять)" : "Пароль (мин. 6 символов)"}</label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="user-password" /></div>
            {editId && <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="user-active" />Активен</label>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-300">Отмена</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={busy} data-testid="user-save">{busy ? "..." : "Сохранить"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
